import { useEffect, useState, type ImgHTMLAttributes } from "react";
import { useAuthStore } from "@/lib/auth-store";

type AuthImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src: string;
};

/**
 * Image that authenticates same-origin `/api/...` URLs.
 *
 * <p>Browser `<img>` tags ignore the JS-memory access token: they don't
 * attach `Authorization` headers, and the token doesn't live in a cookie
 * (we keep it in Zustand to avoid XSS exfiltration). That is fine for
 * absolute presigned URLs in production (S3 issues a publicly-readable
 * URL with its own signature in the query string), but the local-dev
 * direct-serve endpoint at `/api/v1/properties/{pid}/images/{iid}` is
 * gated by `@PreAuthorize("isAuthenticated()")` — so a raw `<img>` 401s
 * and renders a broken icon.
 *
 * <p>This component:
 * <ul>
 *   <li>renders the URL directly when it's absolute (`http(s)://...`),
 *       a blob URL, or a data URL — those don't need auth and we don't
 *       want the extra `fetch` round-trip in production.</li>
 *   <li>for relative `/api/...` URLs, `fetch`es with the current access
 *       token, blobs the response, and renders the resulting object URL.
 *       The object URL is revoked on unmount / `src` change so the blob
 *       gets garbage collected.</li>
 *   <li>renders nothing while the auth-fetch is pending and on error —
 *       the parent layout reserves space via aspect-ratio classes, so we
 *       don't want to render a 401 broken icon in the gap.</li>
 * </ul>
 */
export function AuthImg({ src, alt, ...rest }: AuthImgProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  const needsAuth = src !== "" && src.startsWith("/api");

  useEffect(() => {
    // When src is absolute or empty, the render path ignores objectUrl
    // entirely (see the early returns below), so we just skip the fetch
    // and let any prior objectUrl stay in state until the cleanup
    // function revokes it on next src change / unmount.
    if (!needsAuth) return;

    let cancelled = false;
    let created: string | null = null;
    const headers = new Headers();
    if (accessToken !== null) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    void fetch(src, { credentials: "include", headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Image fetch ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
      })
      .catch(() => {
        if (cancelled) return;
        setObjectUrl(null);
      });

    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [src, accessToken, needsAuth]);

  if (src === "") return null;
  if (!needsAuth) return <img src={src} alt={alt} {...rest} />;
  if (objectUrl === null) return null;
  return <img src={objectUrl} alt={alt} {...rest} />;
}
